/**
 * PWA install-funnel beacons.
 *
 * Anonymous, fire-and-forget tracking of: page_load → prompt_shown →
 * (prompt_dismissed) → installed. Uses a per-tab session id so the backend can
 * count unique sessions, and prefers `sendBeacon` so events survive a
 * navigation/close.
 */
import { isStandalone } from './installDetect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export type PwaEvent = 'page_load' | 'prompt_shown' | 'prompt_dismissed' | 'installed';

export function getPwaSession(): string {
  try {
    let s = sessionStorage.getItem('dd_pwa_session');
    if (!s) {
      s = (crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
      sessionStorage.setItem('dd_pwa_session', s);
    }
    return s;
  } catch {
    return 'anon';
  }
}

function platform(): string {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  if (/Macintosh/.test(ua)) return 'mac';
  if (/Windows/.test(ua)) return 'windows';
  return 'other';
}

function currentSource(): string {
  try {
    const params = new URLSearchParams(window.location.search);
    return (params.get('source') || sessionStorage.getItem('dd_pwa_source') || 'direct').slice(0, 64);
  } catch {
    return 'direct';
  }
}

export function trackPwaEvent(event: PwaEvent, source?: string): void {
  if (!BACKEND_URL) return;
  const body = JSON.stringify({
    event,
    session: getPwaSession(),
    source: source || currentSource(),
    platform: platform(),
  });
  const url = `${BACKEND_URL}/api/pwa/track`;
  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
      return;
    }
  } catch { /* fall through to fetch */ }
  fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
}

/**
 * Call once on app start. Persists ?source for the session, records the initial
 * page_load (browser tab) OR installed (standalone) — each at most once per
 * session — and wires the `appinstalled` event for live install capture.
 */
export function initPwaTracking(): void {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    const src = params.get('source');
    if (src) sessionStorage.setItem('dd_pwa_source', src.slice(0, 64));
  } catch { /* ignore */ }

  try {
    if (isStandalone()) {
      // Running as an installed app. Record an install once per device.
      if (!localStorage.getItem('dd_pwa_installed_tracked')) {
        trackPwaEvent('installed');
        localStorage.setItem('dd_pwa_installed_tracked', '1');
      }
    } else if (!sessionStorage.getItem('dd_pwa_load_tracked')) {
      trackPwaEvent('page_load');
      sessionStorage.setItem('dd_pwa_load_tracked', '1');
    }
  } catch {
    trackPwaEvent('page_load');
  }

  window.addEventListener('appinstalled', () => {
    trackPwaEvent('installed');
    try { localStorage.setItem('dd_pwa_installed_tracked', '1'); } catch { /* ignore */ }
  });
}
